/*
 * @Author: mina.min
 * @Date: 2025-06-09 16:23:39
 * @LastEditors: mina.min
 * @LastEditTime: 2025-06-21 18:20:08
 * @FilePath: /OTAManagementSystem/src/pages/DeviceManagement/DeviceList.tsx
 * @Description: 设备列表组件
 */
import { DeleteOutlined } from '@ant-design/icons'
import { Button, Card, Descriptions, message, Popconfirm, Space, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { deviceStore, type Device } from '../../store/deviceStore'
import './DeviceList.scss'

const { Title, Text } = Typography

const DeviceList: React.FC = observer(() => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([])

  useEffect(() => {
    deviceStore.fetchDeviceList()
  }, [])

  const handleTableChange = (pagination: any) => {
    deviceStore.fetchDeviceList(String(pagination.current), String(pagination.pageSize))
  }

  const handleDelete = async (sn: string) => {
    try {
      const res = await deviceStore.removeDevice(sn)
      if (res.code === '200') {
        message.success(res.info)
      } else {
        message.error(res.info)
      }
    } catch (error: any) {
      message.error(error?.info || error?.message || '删除失败')
    }
  }

  const handleExpand = (expanded: boolean, record: Device) => {
    const keys = expanded
      ? [...expandedRowKeys, record.sn]
      : expandedRowKeys.filter(key => key !== record.sn)
    setExpandedRowKeys(keys)
  }

  const expandedRowRender = (record: Device) => {
    return (
      <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
        <Space direction='vertical' size='large' style={{ width: '100%' }}>
          <Descriptions title='硬件信息' bordered column={1} size='small'>
            {record.hardwareList?.length ? (
              record.hardwareList.map(item => (
                <Descriptions.Item label={item.hardwareName} key={item.hardwareName}>
                  {item.hardwareVersion}
                </Descriptions.Item>
              ))
            ) : (
              <Descriptions.Item label='硬件信息'>暂无数据</Descriptions.Item>
            )}
          </Descriptions>
          <Descriptions title='软件信息' bordered column={1} size='small'>
            {record.softwareList?.length ? (
              record.softwareList.map(item => (
                <Descriptions.Item label={item.softwareName} key={item.softwareName}>
                  {item.softwareVersion}
                </Descriptions.Item>
              ))
            ) : (
              <Descriptions.Item label='软件信息'>暂无数据</Descriptions.Item>
            )}
          </Descriptions>
        </Space>
      </div>
    )
  }

  const columns: ColumnsType<Device> = [
    { title: '车辆SN', dataIndex: 'sn', key: 'sn' },
    {
      title: '最后更新时间',
      dataIndex: 'lastUpdatedTime',
      key: 'lastUpdatedTime',
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right' as const,
      render: (_: any, record: Device) => (
        <Space>
          <Button
            type='link'
            onClick={() => handleExpand(!expandedRowKeys.includes(record.sn), record)}
          >
            查看软硬件信息
          </Button>
          <Popconfirm
            title='确定要删除这个设备吗？'
            description='删除后该设备将从系统中移除'
            onConfirm={() => handleDelete(record.sn)}
            okText='确定'
            cancelText='取消'
          >
            <Button type='link' danger icon={<DeleteOutlined />} loading={deviceStore.loading}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className='device-list'>
      <div className='device-list-header'>
        <Title level={4}>设备列表</Title>
        <Text type='secondary'>设备信息由车辆自动上报，点击可展开查看软硬件详情</Text>
      </div>

      <Card className='device-list-card'>
        <Table
          columns={columns}
          dataSource={deviceStore.deviceList}
          rowKey='sn'
          loading={deviceStore.loading}
          scroll={{ x: 800 }}
          pagination={deviceStore.pagination}
          onChange={handleTableChange}
          expandable={{
            expandedRowRender,
            expandedRowKeys,
            onExpand: handleExpand,
          }}
        />
      </Card>
    </div>
  )
})

export default DeviceList
